import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum } from '@repo/toolkit/types'
import UploadFileInput from '@repo/ui/file-input'
import { UploadIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z, ZodType } from 'zod'

import {
  HobbyAudienceEnum,
  hobbyAudienceEnumMapping
} from '../../../lib/types/enums/hobby/hobby-audience.enum'
import { HobbyInput } from '../../../lib/types/models/hobby/hobby-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createEnumArraySchema } from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<HobbyInput> & {
  className?: string
}

const formSchema: ZodType<HobbyInput> = z.object({
  name: createStringSchema('NOM'),
  description: createStringSchema('description', { nullish: true }),
  visibleTo: createEnumArraySchema({
    fieldName: 'visibilité',
    enumType: HobbyAudienceEnum,
    minimumNumberOfItems: 1
  }),
  picto: FILE_VALIDATION().nullish()
})

const VISIBLE_TO_OPTIONS = Object.values(HobbyAudienceEnum).map((audience) => ({
  label: hobbyAudienceEnumMapping[audience],
  value: audience
}))

const HobbyForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<HobbyInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex gap-4 flex-col', className)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput required {...field} type="text" label="NOM" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="textarea" label="Description" multiple />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="visibleTo"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    label="Visible dans"
                    variant="primary"
                    options={VISIBLE_TO_OPTIONS}
                    selectedOptions={
                      field.value?.map((audience) => ({
                        label: hobbyAudienceEnumMapping[audience],
                        value: audience
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'picto'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <UploadFileInput
                    maxSize={3 * 1024 * 1024} // 3MB
                    allowedAspectRatio="1:1"
                    allowedExtensions={['png']}
                    placeholder="Glisser-déposer ou parcourir"
                    placeholderVariant="tag"
                    placeholderColor={tailwindColors.grey.lighter}
                    types={[FileTypeEnum.IMAGE]}
                    icon={UploadIcon}
                    label={'Télécharger un picto'}
                    description={
                      <div className="flex flex-col justify-center items-center gap-1">
                        <Typography variant="small" color={tailwindColors.grey.light.active}>
                          Téléchargez votre icône uniquement au format&nbsp;
                          <span className="font-medium text-grey-light-active">PNG</span>
                        </Typography>
                        <Typography variant="small" color={tailwindColors.grey.light.active}>
                          Dimensions recommandées :&nbsp;
                          <span className="font-medium text-grey-light-active">
                            307x307 pixels
                          </span>
                        </Typography>
                        <Typography variant="small" color={tailwindColors.grey.light.active}>
                          Maintenez un rapport d'aspect de&nbsp;
                          <span className="font-medium text-grey-light-active">1:1</span>
                        </Typography>
                      </div>
                    }
                    files={field?.value ? [field?.value] : undefined}
                    onFilesChange={(files) => {
                      field.onChange(files.length === 0 ? null : files[0])
                    }}
                    onFileError={(error) => {
                      if (error) toast.error(error)
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default HobbyForm
